import { guid } from "@buildwithflux/shared";
import { create } from "mutative";
import { UserPrivateMetadataRepository } from "./UserPrivateMetadataRepository";
export class MockUserPrivateMetadataRepository extends UserPrivateMetadataRepository {
    entries;
    override = undefined;
    constructor(entries = new Map()) {
        super();
        this.entries = entries;
    }
    /** @inheritDoc */
    touch(userUid) {
        const emptyUserPrivateMetadata = { uid: userUid };
        return new Promise((resolve) => resolve(emptyUserPrivateMetadata));
    }
    /** @inheritDoc */
    update(userUid, userPrivateMetadata) {
        const exists = this.entries.has(userUid);
        const data = {
            ...this.entries.get(userUid),
            uid: userUid,
            ...userPrivateMetadata,
        };
        this.entries.set(userUid, data);
        this.notifyOnChange(userUid, { type: exists ? "updated" : "created", data });
        return Promise.resolve();
    }
    /** @inheritDoc */
    async markOnboardingStepsComplete(userUid, steps) {
        const entry = this.entries.get(userUid);
        if (!entry) {
            return Promise.resolve(); // .update() does nothing if no document exists
        }
        if (!entry.onboarding) {
            entry.onboarding = {};
        }
        if (!entry.onboarding.completedSteps) {
            entry.onboarding.completedSteps = [];
        }
        const completed = new Set(entry.onboarding.completedSteps);
        for (const step of steps) {
            completed.add(step);
        }
        entry.onboarding.completedSteps = Array.from(completed);
        this.entries.set(userUid, entry);
        return Promise.resolve();
    }
    /** @inheritDoc */
    updateSubscription(userUid, customerUid, keyToUpdate, updatedSubscriptionInformation) {
        const exists = this.entries.has(userUid);
        const data = {
            ...this.entries.get(userUid),
            uid: userUid,
            payment: {
                ...this.entries.get(userUid)?.payment,
                customerUid,
                [keyToUpdate]: {
                    ...this.entries.get(userUid)?.payment?.[keyToUpdate],
                    [updatedSubscriptionInformation.uid]: updatedSubscriptionInformation,
                },
            },
        };
        this.entries.set(userUid, data);
        this.notifyOnChange(userUid, { type: exists ? "updated" : "created", data });
        return Promise.resolve();
    }
    /** @inheritDoc */
    deleteForUser(userUid) {
        const wasRemoved = this.entries.delete(userUid);
        if (wasRemoved) {
            this.notifyOnChange(userUid, { type: "deleted" });
        }
        return Promise.resolve();
    }
    /** @inheritDoc */
    getForUser(userUid) {
        const result = this.entries.get(userUid) ?? this.getOverride(userUid);
        return new Promise((resolve) => resolve(result));
    }
    /** @inheritDoc */
    createApiKeyId(userUid) {
        const data = this.entries.get(userUid);
        const id = guid();
        if (data) {
            const keys = data?.api?.keys ?? [];
            keys.push({ id, createdAt: Date.now() });
            data.api ??= { keys };
        }
        else {
            this.entries.set(userUid, {
                uid: userUid,
                api: {
                    keys: [{ id, createdAt: Date.now() }],
                },
            });
        }
        return Promise.resolve(id);
    }
    /**
     * Mock-only method.  Set the repository up to return some default value if none has been found.  If
     * overwriteUserUidOnOverrideis set to true, then the uid of the requested user will be used in place
     * of the uid found on the provided default.
     */
    setDefaultIfNotFound(upm) {
        this.override = upm;
    }
    /** @inheritDoc */
    async dismissIntercomChat(_userUid) {
        return Promise.resolve();
    }
    /** @inheritDoc */
    async setLearningProjectVisibility(_userUid, _hide) {
        throw new Error("Method not implemented.");
    }
    /** @inheritDoc */
    async setLearningProjectUid(_userUid, _learningProjectUid, _clonedProjectUid) {
        throw new Error("Method not implemented.");
    }
    async dismissAnnouncement(userUid, announcementKey) {
        const data = this.entries.get(userUid) ?? { uid: userUid };
        const newData = create(data, (draft) => {
            draft.uid = userUid;
            draft.announcement ??= {};
            draft.announcement.dismissed ??= {};
            draft.announcement.dismissed[announcementKey] = true;
        });
        this.entries.set(userUid, newData);
        return Promise.resolve(undefined);
    }
    /** @inheritDoc */
    async updatePaymentMethodStatus(userUid, subscriptionId, status) {
        const data = this.entries.get(userUid) ?? { uid: userUid };
        const newData = create(data, (draft) => {
            draft.uid = userUid;
            draft.payment ??= { customerUid: "" };
            draft.payment.copilotCreditSubscriptions ??= {};
            const subscription = draft.payment.copilotCreditSubscriptions[subscriptionId];
            if (subscription) {
                subscription.paymentMethodStatus = status;
            }
        });
        this.entries.set(userUid, newData);
        return Promise.resolve(undefined);
    }
    getOverride(userUid) {
        if (!this.override)
            return undefined;
        const newData = {
            ...this.override,
            uid: userUid,
        };
        this.entries.set(userUid, newData);
        return newData;
    }
}
//# sourceMappingURL=MockUserPrivateMetadataRepository.js.map